import {
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { FormValues } from './types';

type Props = {
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
};

function CreateWebsitePopup({ handleClose, setRefreshTrigger }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: { status: 1 },
  });

  const [loading, setLoading] = useState(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  const onSubmit = async (values: FormValues) => {
    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token missing. Please log in again.',
        });
        return;
      }
      setLoading(true);

      const formData = new FormData();
      formData.append('website_link', values.website_link);
      formData.append('alt_text', values.alt_text);
      formData.append('status', String(values.status));
      formData.append('start_date', values.start_date);
      formData.append('end_date', values.end_date);
      if (values.image && values.image[0]) {
        formData.append('image', values.image[0]);
      }

      const { data } = await axios.post(
        `${url}/api/v1/admin/website-popup`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: data.message || 'Website popup created successfully!',
        });
        setRefreshTrigger((prev) => prev + 1);
        reset();
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: data.message || 'Failed to create website popup.',
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: err?.response?.data?.message || 'Something went wrong',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ p: 1 }}>
      <Grid container spacing={1}>
        {/* Website Link */}
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            id='website_link'
            label='Website Link'
            type='url'
            variant='outlined'
            placeholder='https://example.com/promo'
            error={!!errors.website_link}
            helperText={errors.website_link?.message}
            {...register('website_link')}
          />
        </Grid>

        {/* Alt Text */}
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='alt_text'
            label='Image Alt Text'
            type='text'
            variant='outlined'
            placeholder='e.g. Summer Campaign Popup'
            error={!!errors.alt_text}
            helperText={errors.alt_text?.message}
            {...register('alt_text', { required: 'Alt text is required' })}
          />
        </Grid>

        {/* Start Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='start_date'
            label='Start Date'
            type='date'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            error={!!errors.start_date}
            helperText={errors.start_date?.message}
            {...register('start_date', { required: 'Start date is required' })}
          />
        </Grid>

        {/* End Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='end_date'
            label='End Date'
            type='date'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            error={!!errors.end_date}
            helperText={errors.end_date?.message}
            {...register('end_date', { required: 'End date is required' })}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <FormControl fullWidth variant='outlined' margin='normal'>
            <InputLabel id='status-label'>Status</InputLabel>
            <Select
              labelId='status-label'
              id='status'
              label='Status'
              defaultValue={1}
              {...register('status')}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12}>
          <MultipleAndSingleImgValidateWithPreview
            register={register}
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            label='Upload Popup Image'
            fieldName='image'
            hideCustomLabel
            onlyImage
            mode='single'
            isRequired
          />
        </Grid>

        {/* Submit */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? 'Creating...' : 'Create Website Popup'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CreateWebsitePopup;
